/**
 * The two non-`NotFound` outcomes a `GatedRoute` can render for a visitor who
 * is not `'authenticated'` — spec 028-h1-route-404, plan D-5, T-3.5.
 *
 * IMPORTANT — this is a DISPLAY gate, not an authorization boundary. Every
 * path string these components stand in for is still public: the client-side
 * router match happens before either of these renders, and the withheld page
 * COMPONENT is one breakpoint or dev-tools edit away. The real control is
 * server-side — `loadOwned*` ownership checks and the 404-not-403 invariant
 * on every API route (`CLAUDE.md` > Security posture). Do not let a future
 * change treat gating the route component as sufficient on its own.
 *
 * Statically imported by `App.tsx` on purpose — no `lazyWithRetry`, no
 * `React.lazy`, no dynamic `import()`. A component whose entire job is "the
 * network failed" cannot itself be delivered over a network that just
 * failed: `lazyWithRetry.ts`'s promise never resolves on a chunk-load
 * failure, which is exactly the endless spinner this file exists to avoid.
 */
import { Link } from 'wouter';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';

// Deliberately takes no props — in particular, no requested-path prop (plan
// D-4: the copy must never name it, so there is nothing to pass in).
export function SignedOutFallback(): JSX.Element {
  const { t } = useLanguage();

  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16 text-center"
      data-testid="auth-signed-out"
    >
      <div role="status" className="max-w-md">
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">
          {t('authGate.signedOut.title')}
        </h1>
        <p className="mb-6 text-gray-600">{t('authGate.signedOut.body')}</p>
        <Button asChild>
          <Link href="/login">{t('authGate.signedOut.signIn')}</Link>
        </Button>
      </div>
    </div>
  );
}

interface AuthUnavailableFallbackProps {
  onRetry: () => void;
}

export function AuthUnavailableFallback({ onRetry }: AuthUnavailableFallbackProps): JSX.Element {
  const { t } = useLanguage();

  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center px-4 py-16 text-center"
      data-testid="auth-unavailable"
    >
      <div role="status" className="max-w-md">
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">
          {t('authGate.unavailable.title')}
        </h1>
        <p className="mb-6 text-gray-600">{t('authGate.unavailable.body')}</p>
        <Button type="button" variant="outline" onClick={onRetry}>
          {t('authGate.unavailable.retry')}
        </Button>
      </div>
    </div>
  );
}
